import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


// ===============================
// ⭐ 1. POST REVIEW
// ===============================
export const postReview = createAsyncThunk(
    "reviews/postReview",
    async (formData, { rejectWithValue }) => {
        try {
            const { data } = await api.post("/reviews/post-review", formData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Unable to post review");
        }
    }
);


// ===============================
// ⭐ 2. GET ALL REVIEWS (pagination)
// ===============================
export const getAllReviews = createAsyncThunk(
    "reviews/getAllReviews",
    async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const { data } = await api.get(
                `/reviews/reviews?page=${page}&limit=${limit}`
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Unable to fetch reviews");
        }
    }
);


// ===============================
// ⭐ 3. GET USER REVIEWS
// ===============================
export const getUserReviews = createAsyncThunk(
    "reviews/getUserReviews",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get("/reviews/user-reviews");
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Unable to fetch user reviews");
        }
    }
);


// ===============================
// ⭐ 4. GET SERVICEMEN REVIEWS
// ===============================
export const getVendorReviews = createAsyncThunk(
    "reviews/getVendorReviews",
    async (vendorId, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/reviews/vendor-reviews/${vendorId}`);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Unable to fetch servicemen reviews");
        }
    }
);


// ===============================
// ⭐ 5. DELETE REVIEW
// ===============================
export const deleteReview = createAsyncThunk(
    "reviews/deleteReview",
    async (reviewId, { rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/reviews/delete-review/${reviewId}`);
            return { reviewId, message: data.message };
        } catch (error) {
            return rejectWithValue(error.response?.data || "Unable to delete review");
        }
    }
);


// ===============================
// ⭐ 6. SLICE
// ===============================
const reviewSlice = createSlice({

    name: "reviews",
    initialState: {

        allReviews: [],
        userReviews: [],
        vendorReviews: [],
        totalReviews: 0,
        averageRating: 0,

        loading: false,
        error: null,
        success: null,

        page: 1,
        totalPages: 1,

    },

    reducers: {
        clearReviewMessages: (state) => {
            state.error = null;
            state.success = null;
        },
    },

    extraReducers: (builder) => {

        // ============================
        // POST REVIEW
        // ============================
        builder
            .addCase(postReview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(postReview.fulfilled, (state, action) => {
                state.loading = false;
                state.success = action.payload.message;

                // push new review to user list
                state.userReviews.unshift(action.payload.data);
            })
            .addCase(postReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message || "Post review failed";
            });

        // ============================
        // GET ALL REVIEWS (Paginated)
        // ============================
        builder
            .addCase(getAllReviews.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.allReviews = action.payload.reviews;
                state.page = action.payload.currentPage;
                state.totalPages = action.payload.totalPages;
                state.totalReviews = action.payload.totalReviews;
            })
            .addCase(getAllReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            });

        // ============================
        // GET USER REVIEWS
        // ============================
        builder
            .addCase(getUserReviews.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUserReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.userReviews = action.payload;
            })
            .addCase(getUserReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            });

        // ============================
        // GET SERVICEMEN REVIEWS
        // ============================
        builder
            .addCase(getVendorReviews.pending, (state) => {
                state.loading = true;
            })
            .addCase(getVendorReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.vendorReviews = action.payload.reviews;
                state.totalReviews = action.payload.totalReviews;
                state.averageRating = action.payload.averageRating;
            })
            .addCase(getVendorReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            });

        // ============================
        // DELETE REVIEW
        // ============================
        builder
            .addCase(deleteReview.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteReview.fulfilled, (state, action) => {
                state.loading = false;
                state.success = action.payload.message;

                // remove deleted review
                state.allReviews = state.allReviews.filter(
                    (r) => r._id !== action.payload.reviewId
                );

                state.userReviews = state.userReviews.filter(
                    (r) => r._id !== action.payload.reviewId
                );
            })
            .addCase(deleteReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
            });
    },
});

export const { clearReviewMessages } = reviewSlice.actions;
export default reviewSlice.reducer;
